import Aspera, { CONNECT_DETECT_AFTER_GRANT } from './aspera';
import ToastService from './toast-service';
import { ACCESS, readAccess, waitForAccess, watchAccess } from './local-network-access';

// Loaded only when someone is actually being onboarded. Importing the modal up
// front would pull a styled-components tree into the transfer-manager → aspera
// import cycle, and most users never see it.
const loadOnboardingModal = () => import('~/modules/components/global/aspera-onboarding');

// "Not now" in the modal: soft, and reminded about on later transfers.
const DECLINED_KEY = 'asperaOnboardingDeclined';
// "Don't show again" in the reminder: no modal, no toast, until Preferences
// puts it back. Deliberately not called "blocked" — that word already means the
// browser permission (ACCESS.DENIED) everywhere else in this code.
const SILENCED_KEY = 'asperaRemindersSilenced';

// One toast at a time, and one that a ten-file download cannot stack. Repeat
// calls carrying a live id are a no-op in react-toastify.
const REMINDER_TOAST_ID = 'aspera-reminder';

// What the Preferences toggle says when it cannot switch Aspera on. remind() is
// worded for a transfer already on its way — "your transfer is going over
// HTTPS" — and nobody is transferring anything here: they flipped a setting and
// are owed an answer about the setting. Its "Don't show again" action makes no
// sense either, a moment after being asked to turn reminders back on.
const reportCannotEnable = (access) => {
	// Switched off in another tab while we were waiting. The answer they are owed
	// is about a switch that is no longer on, and an installer link would be
	// advice for a feature they have just turned off.
	if( isSilenced() ) {
		return;
	}

	if( access === ACCESS.DENIED ) {
		ToastService.create(
			'info',
			'Aspera is blocked for this site. Allow local network access in your'
				+ ' browser\'s site settings, then turn it on again.'
		);
		return;
	}

	// Back at unanswered: the dialog was dismissed, or the permission was cleared
	// in site settings while we waited. Nothing was learned about Connect either
	// way, so an installer is the wrong advice — and another attempt would raise
	// the browser prompt, which has to follow another deliberate click.
	if( access === ACCESS.PROMPT ) {
		ToastService.create(
			'info',
			'Aspera still needs permission to reach this computer.'
				+ ' Transfers will use HTTPS until you allow it.'
		);
		return;
	}

	ToastService.createWithActions(
		'info',
		'Aspera isn\'t running on this computer. Transfers will use HTTPS until it is.',
		[
			{
				label: 'Get Aspera',
				variant: 'primary',
				testId: 'aspera-preferences-install',
				onClick: () => AsperaOnboardingService.openInstallHelp()
			}
		],
		{ autoClose: false }
	);
};

// The blocked reminder carries no autoClose, because the situation it describes
// ends in the browser's own settings and a timer cannot follow someone there.
// So follow the permission instead: once the block is lifted the toast has
// stopped being true, and leaving it up has the UI calling the site blocked
// through transfers that are working. One watcher at a time — a second download
// must not stack another.
let unwatchBlocked = null;

const stopWatchingBlocked = () => {
	unwatchBlocked?.();
	unwatchBlocked = null;
};

const dismissBlockedReminderOnRecovery = () => {
	stopWatchingBlocked();

	unwatchBlocked = watchAccess((access) => {
		if( access === ACCESS.DENIED ) {
			return;
		}

		ToastService.dismiss(REMINDER_TOAST_ID);
		stopWatchingBlocked();
	});
};

// One in-flight decision at a time: a multi-file download calls this once per
// file, and the user should answer once.
let pending = null;

let listeners = [];

// Flags whose write storage refused. A private window or blocked cookies can
// throw on setItem while getItem still answers, and reading back through
// storage would then report the opposite of what the user just chose — leaving
// "Don't show again" and the Preferences off switch doing nothing at all. These
// are remembered for the tab instead; only persistence is lost.
const unstored = {};

const readFlag = (key) => {
	if( key in unstored ) {
		return unstored[key];
	}

	try {
		return window.localStorage.getItem(key) === 'true';
	}
	catch{
		// cannot read it either: treat as never asked
		return false;
	}
};

const writeFlag = (key, value) => {
	try {
		if( value ) {
			window.localStorage.setItem(key, 'true');
		}
		else {
			window.localStorage.removeItem(key);
		}

		// It stuck, so storage is the answer again — a value kept here from an
		// earlier failure would otherwise shadow it forever.
		delete unstored[key];
	}
	catch{
		unstored[key] = value;
	}

	listeners.forEach((listener) => listener());
};

const isDeclined = () => readFlag(DECLINED_KEY);
const isSilenced = () => readFlag(SILENCED_KEY);

// These flags live in localStorage, which is shared across tabs, and the
// browser reports a write only to the *other* tabs. Without this, declining in
// one tab leaves another showing an offer that no longer applies.
if( typeof window?.addEventListener === 'function' ) {
	window.addEventListener('storage', (event) => {
		// A null key is the whole store being cleared.
		if( event.key !== null && event.key !== DECLINED_KEY && event.key !== SILENCED_KEY ) {
			return;
		}

		// Another tab wrote, which proves two things: storage works, and it now
		// holds a value newer than anything this tab kept when its own write was
		// refused. Drop the stand-in first — left in place it would shadow the
		// shared answer for the rest of the session, and a Preferences reset next
		// door would notify every subscriber without changing what they read.
		if( event.key === null ) {
			delete unstored[DECLINED_KEY];
			delete unstored[SILENCED_KEY];
		}
		else {
			delete unstored[event.key];
		}

		// "Don't show again" in another tab has to take this tab's reminder down
		// too. Leaving it up is worse than untidy: every decision now
		// short-circuits on the shared flag, so its "Set up Aspera" action would
		// open nothing.
		if( isSilenced() ) {
			ToastService.dismiss(REMINDER_TOAST_ID);
		}

		listeners.forEach((listener) => listener());
	});
}

const AsperaOnboardingService = {
	isDeclined,
	isSilenced,

	/**
	 * Lets a component follow the flags rather than reading them once on mount:
	 * Preferences and a mounted dropzone both need to notice the other's changes.
	 */
	subscribe: (listener) => {
		listeners.push(listener);

		return () => {
			listeners = listeners.filter((registered) => registered !== listener);
		};
	},

	/**
	 * "Don't show again" — stops the reminders without touching the browser, and
	 * takes down any that is still on screen: its setup action would be a dead
	 * end now that every decision short-circuits on this flag.
	 */
	silence: () => {
		writeFlag(SILENCED_KEY, true);
		ToastService.dismiss(REMINDER_TOAST_ID);
		// Nothing left for it to take down, and it would otherwise sit on the
		// permission for the life of the tab.
		stopWatchingBlocked();
	},

	/** Whether Aspera is on offer at all — what the Preferences toggle shows. */
	isEnabled: () => !isSilenced(),

	/**
	 * Whether Aspera can carry a transfer right now. A cheap re-check for the
	 * moment of use — no modal, no session start — because a decision made a
	 * request ago can be stale: Connect can stop, and the permission can be taken
	 * away, while a download spec is in flight.
	 */
	isReady: async() => {
		// The switch counts as much as the permission here: it can be turned off
		// while a spec request is in flight, and the decision taken before that
		// request would otherwise still be acted on.
		if( isSilenced() ) {
			return false;
		}

		const access = await readAccess();

		// Read again on the way out. The switch is shared across tabs and the query
		// above is asynchronous, so an opt-out can land inside it — and this is the
		// check standing between that opt-out and a transfer going to Aspera
		// regardless. The one above only saves the query in the common case.
		if( isSilenced() ) {
			return false;
		}

		return access !== ACCESS.DENIED
			&& access !== ACCESS.PROMPT
			&& Aspera.getStatus() === 'RUNNING';
	},

	/** Puts Aspera back on offer, from Preferences. */
	reset: () => {
		writeFlag(DECLINED_KEY, false);
		writeFlag(SILENCED_KEY, false);
	},

	/** Turning it off in Preferences: no offers, no reminders, always http. */
	disable: () => AsperaOnboardingService.silence(),

	/**
	 * Turning it on in Preferences asks for the permission there and then rather
	 * than waiting for the next transfer — the click is the consent, and this is
	 * the one place a user goes looking for Aspera deliberately.
	 *
	 * Reports what it found either way, so the toggle is never a switch that
	 * appears to do nothing.
	 */
	enable: async() => {
		AsperaOnboardingService.reset();

		if( (await readAccess()) === ACCESS.DENIED ) {
			reportCannotEnable(ACCESS.DENIED);
			return false;
		}

		// The prompt, if there is one, is raised here — inside the click. Started
		// and not awaited: while that prompt holds the SDK's loopback probe pending
		// no status arrives, so the session runs to its own sixty-second cap.
		// Awaiting it would stack that in front of the permission wait below and
		// leave the switch disabled for a minute and a half over an answer the user
		// gave in three seconds.
		Aspera.ensureSession();

		// Waited for, not read: the SDK gives up on loopback in about five seconds
		// and the browser's dialog is usually still open then, so starting the
		// Connect clock now would time it against the user's reading speed.
		const access = await waitForAccess();

		// Answered no, or not answered at all. Neither state can become ready, so
		// neither is worth spending the Connect wait on — that wait exists for a permission that was just granted, and
		// running it here would only sit on the answer the user is owed. A
		// dismissed dialog leaves access on PROMPT, which remind() deliberately
		// says nothing about: for a transfer that is the state the modal quietly
		// offers itself for, but here the silence would be a toggle that flips on
		// and reports nothing.
		if( access === ACCESS.DENIED || access === ACCESS.PROMPT ) {
			reportCannotEnable(access);
			return false;
		}

		// waitForRunning can answer from a status that outlived its permission, so
		// readiness is confirmed before claiming it: telling someone Aspera is
		// ready when the browser will refuse it is worse than saying nothing.
		if( await Aspera.waitForRunning(CONNECT_DETECT_AFTER_GRANT)
			&& await AsperaOnboardingService.isReady() ) {
			ToastService.create('info', 'Aspera is ready. Your transfers will use it from now on.');
			return true;
		}

		// Granted but nothing answering, or taken away again while we waited — so
		// which of the two it is has to be re-read, not assumed from the earlier
		// answer.
		reportCannotEnable(await readAccess());
		return false;
	},

	/**
	 * Reopens the offer on demand — the reminder toast's own action.
	 *
	 * Clearing the decline first is what makes a later "Not now" record cleanly:
	 * the user is opting back in by being here at all.
	 */
	openOnboarding: async(transfer = 'transfer') => {
		writeFlag(DECLINED_KEY, false);

		return AsperaOnboardingService.shouldUseAspera(transfer);
	},

	/**
	 * Shows where to get Aspera, for the users the offer never reaches: the
	 * permission is already granted, so nothing would raise the modal for them.
	 */
	openInstallHelp: async() => {
		try {
			const { default: asperaOnboarding, STEP } = await loadOnboardingModal();
			await asperaOnboarding({ initialStep: STEP.NOT_INSTALLED });
		}
		catch{
			// optional UI; the transfer is unaffected either way
		}
	},

	/**
	 * Tells a user who just transferred without Aspera that it exists, and how to
	 * get it — but only where they were skipped silently. Someone who was shown
	 * the modal a moment ago and said no is not reminded of the thing they just
	 * declined; the next transfer catches them.
	 *
	 * The transfer is already on its way over HTTPS by the time this runs, so the
	 * copy is about the ones after it.
	 */
	remind: async(transfer = 'transfer') => {
		if( isSilenced() ) {
			return;
		}

		const access = await readAccess();

		// Same race as isReady(): "Don't show again" can land inside that query,
		// and silence() has already taken the visible toast down by then. Building
		// a persistent one now would put back precisely what the user dismissed.
		if( isSilenced() ) {
			return;
		}

		const silenceAction = {
			label: 'Don\'t show again',
			testId: 'aspera-reminder-silence',
			onClick: () => AsperaOnboardingService.silence()
		};

		// No in-page action can lift a browser block, so this one only explains.
		if( access === ACCESS.DENIED ) {
			ToastService.createWithActions(
				'info',
				`Aspera is blocked for this site, so your ${transfer} is going over HTTPS.`
					+ ' Allow local network access in your browser\'s site settings to use it.',
				[silenceAction],
				{ toastId: REMINDER_TOAST_ID, autoClose: false }
			);
			dismissBlockedReminderOnRecovery();
			return;
		}

		// Permission granted, but nothing answering on the other end. The offer
		// modal is never shown to these users, so the install step is reachable
		// only from here.
		if( access !== ACCESS.PROMPT && Aspera.getStatus() !== 'RUNNING' ) {
			ToastService.createWithActions(
				'info',
				`Aspera isn't running on this computer, so your ${transfer} is going over HTTPS.`,
				[
					silenceAction,
					{
						label: 'Get Aspera',
						variant: 'primary',
						testId: 'aspera-reminder-install',
						onClick: () => AsperaOnboardingService.openInstallHelp()
					}
				],
				{ toastId: REMINDER_TOAST_ID, autoClose: false }
			);
			return;
		}

		if( access !== ACCESS.PROMPT || !isDeclined() ) {
			return;
		}

		ToastService.createWithActions(
			'info',
			`Your ${transfer} is going over HTTPS. Aspera moves large media much faster`
				+ ' and picks up where it left off — worth setting up for the next one.',
			[
				silenceAction,
				{
					label: 'Set up Aspera',
					variant: 'primary',
					testId: 'aspera-reminder-setup',
					onClick: () => AsperaOnboardingService.openOnboarding(transfer)
				}
			],
			{ toastId: REMINDER_TOAST_ID, autoClose: false }
		);
	},

	/**
	 * Decides whether this transfer should go over Aspera, onboarding the user
	 * the first time it could.
	 *
	 * Nothing here touches loopback until the user asks for it: the permission
	 * state is readable on its own, so an established yes or no is honoured
	 * silently and only a genuine "never been asked" raises the modal.
	 */
	shouldUseAspera: async(transfer = 'transfer') => {
		if( pending ) {
			return pending;
		}

		pending = (async() => {
			// Switched off in Preferences: that answer covers every permission
			// state, including the granted one where Connect would otherwise just
			// be used.
			if( isSilenced() ) {
				return false;
			}

			const access = await readAccess();

			// Already blocked at the browser level: asking again is impossible and
			// the loopback call would only fail.
			if( access === ACCESS.DENIED ) {
				await AsperaOnboardingService.remind(transfer);
				return false;
			}

			// Settled, or a browser that never gates loopback — just use Aspera if
			// Connect answers.
			if( access !== ACCESS.PROMPT ) {
				await Aspera.ensureSession();

				// Not the status as it stands: ensureSession resolves from cache for
				// every transfer after the first, and the SDK keeps polling after a
				// FAILED. Reading it flat would drop a Connect that is still starting.
				//
				// isReady on top of it because that wait can take seconds, and the
				// permission can be taken away inside them.
				if( await Aspera.waitForRunning() && await AsperaOnboardingService.isReady() ) {
					return true;
				}

				// Permission but no Connect: they never see the modal, so this is
				// where they learn Aspera is missing rather than just slow.
				await AsperaOnboardingService.remind(transfer);
				return false;
			}

			// Silenced is the stronger of the two: someone who asked us to stop must
			// not be handed the modal because their permission drifted back to
			// unanswered. remind() no-ops for them, so this stays quiet.
			if( isDeclined() || isSilenced() ) {
				await AsperaOnboardingService.remind(transfer);
				return false;
			}

			try {
				const { default: asperaOnboarding, CHOICE } = await loadOnboardingModal();
				const choice = await asperaOnboarding({ transfer });

				if( choice === CHOICE.ASPERA ) {
					return true;
				}

				// Only a decline is remembered. Someone who tried and turned out not
				// to have Connect has a granted permission now, so the branch above
				// takes over and they are never asked again either way.
				if( (await readAccess()) === ACCESS.PROMPT ) {
					writeFlag(DECLINED_KEY, true);
				}

				return false;
			}
			catch{
				// The offer is a nicety loaded on demand. A failed chunk or a broken
				// dialog must not fail the transfer, which still has HTTPS.
				return false;
			}
		})();

		const decision = pending;
		// Cleared through the local handle so a later call's promise is never
		// dropped by an earlier one finishing. Both outcomes are handled here
		// rather than with finally(), whose derived promise would carry a
		// rejection nobody is watching.
		const clear = () => {
			if( pending === decision ) {
				pending = null;
			}
		};
		decision.then(clear, clear);

		return decision;
	}
};

export default AsperaOnboardingService;
